import { useEffect, useRef, useState } from 'react';
import { MapPin, Cpu, Droplets, ShieldCheck, RefreshCw, Layers } from 'lucide-react';
import { loadGoogleMaps, isGoogleMapsLoaded, getGoogleMapsApi } from '../lib/googleMaps';
import type { GPSLocation } from '../lib/googleMaps';
import { MapErrorBoundary } from './MapErrorBoundary';

export interface MapDevice {
  id: string;
  type: 'controller' | 'water_meter' | 'double_check' | 'zone';
  label: string;
  gps_location: GPSLocation;
}

interface PropertyMapProps {
  propertyLocation: GPSLocation;
  propertyName: string;
  devices?: MapDevice[];
  height?: string;
}

const DEVICE_COLORS: Record<MapDevice['type'], string> = {
  controller: '#2563eb',
  water_meter: '#16a34a',
  double_check: '#d97706',
  zone: '#0891b2',
};

const DEVICE_LABELS: Record<MapDevice['type'], string> = {
  controller: 'Controller',
  water_meter: 'Water Meter',
  double_check: 'Double Check',
  zone: 'Zone',
};

function createMarkerIcon(
  maps: typeof google.maps,
  color: string,
  isProperty: boolean,
): google.maps.Symbol {
  if (isProperty) {
    return {
      path: maps.SymbolPath.CIRCLE,
      scale: 10,
      fillColor: '#dc2626',
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeWeight: 2,
    };
  }
  return {
    path: maps.SymbolPath.CIRCLE,
    scale: 8,
    fillColor: color,
    fillOpacity: 1,
    strokeColor: '#ffffff',
    strokeWeight: 2,
  };
}

export function PropertyMap(props: PropertyMapProps) {
  return (
    <MapErrorBoundary height={props.height ?? '320px'}>
      <PropertyMapInner {...props} />
    </MapErrorBoundary>
  );
}

function PropertyMapInner({ propertyLocation, propertyName, devices = [], height = '320px' }: PropertyMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const [mapReady, setMapReady] = useState(isGoogleMapsLoaded());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadGoogleMaps()
      .then(() => {
        // Resolving is not proof the API is usable — check the global itself.
        if (isGoogleMapsLoaded()) setMapReady(true);
        else setError('Failed to load Google Maps.');
      })
      .catch((err) => {
        console.error('Failed to load Google Maps for the property map:', err);
        setError('Failed to load Google Maps.');
      });
  }, []);

  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    // Never touch the bare `google` global: it throws a ReferenceError rather
    // than reading as undefined when the third-party script isn't there.
    const maps = getGoogleMapsApi();
    if (!maps) {
      setError('Failed to load Google Maps.');
      return;
    }

    const center = { lat: propertyLocation.latitude, lng: propertyLocation.longitude };

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new maps.Map(mapRef.current, {
        center,
        zoom: 17,
        mapTypeId: maps.MapTypeId.HYBRID,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }],
          },
        ],
      });
    } else {
      mapInstanceRef.current.setCenter(center);
    }

    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];

    const infoWindow = new maps.InfoWindow();

    const propertyMarker = new maps.Marker({
      position: center,
      map: mapInstanceRef.current,
      title: propertyName,
      icon: createMarkerIcon(maps, '#dc2626', true),
      zIndex: 1000,
    });

    propertyMarker.addListener('click', () => {
      infoWindow.setContent(`
        <div style="font-family: sans-serif; min-width: 140px;">
          <div style="font-weight: 700; font-size: 13px; color: #111; margin-bottom: 2px;">Property</div>
          <div style="font-size: 12px; color: #374151;">${propertyName}</div>
          <div style="font-size: 11px; color: #6b7280; margin-top: 4px;">
            ${propertyLocation.latitude.toFixed(6)}, ${propertyLocation.longitude.toFixed(6)}
          </div>
        </div>
      `);
      infoWindow.open(mapInstanceRef.current, propertyMarker);
    });

    markersRef.current.push(propertyMarker);

    const bounds = new maps.LatLngBounds();
    bounds.extend(center);

    devices.forEach((device) => {
      const pos = { lat: device.gps_location.latitude, lng: device.gps_location.longitude };
      const color = DEVICE_COLORS[device.type];
      const typeLabel = DEVICE_LABELS[device.type];

      const marker = new maps.Marker({
        position: pos,
        map: mapInstanceRef.current!,
        title: device.label,
        icon: createMarkerIcon(maps, color, false),
        zIndex: 500,
      });

      marker.addListener('click', () => {
        infoWindow.setContent(`
          <div style="font-family: sans-serif; min-width: 140px;">
            <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 4px;">
              <div style="width: 10px; height: 10px; border-radius: 50%; background: ${color}; flex-shrink: 0;"></div>
              <div style="font-weight: 700; font-size: 13px; color: #111;">${typeLabel}</div>
            </div>
            <div style="font-size: 12px; color: #374151;">${device.label}</div>
            <div style="font-size: 11px; color: #6b7280; margin-top: 4px;">
              ${device.gps_location.latitude.toFixed(6)}, ${device.gps_location.longitude.toFixed(6)}
            </div>
          </div>
        `);
        infoWindow.open(mapInstanceRef.current, marker);
      });

      markersRef.current.push(marker);
      bounds.extend(pos);
    });

    if (devices.length > 0) {
      mapInstanceRef.current.fitBounds(bounds, { top: 40, bottom: 40, left: 40, right: 40 });
      const listener = maps.event.addListenerOnce(mapInstanceRef.current, 'idle', () => {
        const zoom = mapInstanceRef.current!.getZoom();
        if (zoom !== undefined && zoom > 19) {
          mapInstanceRef.current!.setZoom(19);
        }
      });
      // Cleanup captures `maps` from the closure — it must never re-read the
      // global, which may be gone by unmount time.
      return () => {
        maps.event.removeListener(listener);
      };
    }
  }, [mapReady, propertyLocation, propertyName, devices]);

  if (error) {
    return (
      <div
        className="w-full rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 flex items-center justify-center text-red-600 dark:text-red-400 text-sm"
        style={{ height }}
      >
        {error}
      </div>
    );
  }

  if (!mapReady) {
    return (
      <div
        className="w-full rounded-lg border border-line bg-subtle flex items-center justify-center text-theme-secondary text-sm gap-2"
        style={{ height }}
      >
        <RefreshCw size={16} className="animate-spin" />
        Loading map...
      </div>
    );
  }

  return (
    <div className="w-full rounded-lg overflow-hidden border border-line shadow-sm" style={{ height }}>
      <div ref={mapRef} className="w-full h-full" />
    </div>
  );
}

export function MapLegend({ devices }: { devices: MapDevice[] }) {
  const hasControllers = devices.some((d) => d.type === 'controller');
  const hasWaterMeters = devices.some((d) => d.type === 'water_meter');
  const hasDoubleChecks = devices.some((d) => d.type === 'double_check');
  const hasZones = devices.some((d) => d.type === 'zone');

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-theme-secondary">
      <div className="flex items-center gap-1.5">
        <div className="w-3 h-3 rounded-full bg-red-600 border-2 border-white shadow-sm" />
        <span>Property</span>
      </div>
      {hasControllers && (
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full border-2 border-white shadow-sm" style={{ background: DEVICE_COLORS.controller }} />
          <Cpu size={11} className="text-blue-600" />
          <span>Controller</span>
        </div>
      )}
      {hasZones && (
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full border-2 border-white shadow-sm" style={{ background: DEVICE_COLORS.zone }} />
          <Layers size={11} className="text-cyan-600" />
          <span>Zone</span>
        </div>
      )}
      {hasWaterMeters && (
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full border-2 border-white shadow-sm" style={{ background: DEVICE_COLORS.water_meter }} />
          <Droplets size={11} className="text-green-600" />
          <span>Water Meter</span>
        </div>
      )}
      {hasDoubleChecks && (
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full border-2 border-white shadow-sm" style={{ background: DEVICE_COLORS.double_check }} />
          <ShieldCheck size={11} className="text-amber-600" />
          <span>Double Check</span>
        </div>
      )}
      {devices.length === 0 && (
        <span className="text-theme-secondary flex items-center gap-1">
          <MapPin size={11} />
          No devices with GPS coordinates
        </span>
      )}
    </div>
  );
}
